import React, { useState } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { createPaypalOrder, capturePaypalOrder } from '../utils/api';

/**
 * Self-contained PayPal Smart Buttons checkout for a single Taxi-98 booking.
 *
 * v1 contract:
 *  - `bookingId`  -> required
 *  - `amount`     -> display only (server uses booking's estimatedFare from DB)
 *  - `currency`   -> default USD
 *  - `onPaid`     -> callback fires once on successful capture
 *
 * Lazy-loads the PayPal SDK only when this component mounts.
 */
const PayPalCheckout = ({ bookingId, amount, currency = 'USD', onPaid }) => {
  const [status, setStatus] = useState('idle'); // idle | paid | error | cancelled
  const [errorMsg, setErrorMsg] = useState('');
  const [captureInfo, setCaptureInfo] = useState(null);

  const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
  if (!clientId) {
    return (
      <div data-testid="paypal-not-configured" className="text-sm text-red-600">
        PayPal is not configured. Please contact support.
      </div>
    );
  }

  if (status === 'paid' && captureInfo) {
    return (
      <div
        data-testid="paypal-paid-banner"
        className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3"
      >
        <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-bold text-green-900">Payment received via PayPal</p>
          <p className="text-sm text-green-800">
            ${Number(captureInfo.amount || amount).toFixed(2)} {captureInfo.currency || currency}
            {captureInfo.captureId ? ` · Txn ${captureInfo.captureId}` : ''}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="paypal-checkout-wrapper">
      {status === 'error' && (
        <div
          data-testid="paypal-error-banner"
          className="mb-3 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2"
        >
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-800">
            <p className="font-semibold">Payment couldn't be completed.</p>
            {errorMsg && <p className="text-xs mt-1">{errorMsg}</p>}
            <p className="text-xs mt-1">Your booking is still confirmed — you can pay in cash on arrival or try PayPal again below.</p>
          </div>
        </div>
      )}

      {status === 'cancelled' && (
        <div
          data-testid="paypal-cancel-banner"
          className="mb-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800"
        >
          Payment cancelled. Your booking is still confirmed — pay in cash on arrival or try PayPal again below.
        </div>
      )}

      <PayPalScriptProvider
        options={{
          clientId,
          currency,
          intent: 'capture',
        }}
      >
        <PayPalButtons
          data-testid="paypal-buttons"
          style={{ layout: 'vertical', shape: 'rect', label: 'pay' }}
          forceReRender={[bookingId, amount, currency, status]}
          createOrder={async () => {
            setStatus('idle');
            setErrorMsg('');
            try {
              const res = await createPaypalOrder(bookingId);
              return res.orderId;
            } catch (err) {
              const detail = err?.response?.data?.detail || err?.message || 'Failed to create order';
              setErrorMsg(detail);
              setStatus('error');
              throw err;
            }
          }}
          onApprove={async (data) => {
            try {
              const res = await capturePaypalOrder(data.orderID);
              if (res?.status === 'paid') {
                setCaptureInfo(res);
                setStatus('paid');
                if (typeof onPaid === 'function') onPaid(res);
              } else {
                setErrorMsg(`Capture status: ${res?.status || 'unknown'}`);
                setStatus('error');
              }
            } catch (err) {
              const detail = err?.response?.data?.detail || err?.message || 'Capture failed';
              setErrorMsg(detail);
              setStatus('error');
            }
          }}
          onCancel={() => {
            setStatus('cancelled');
          }}
          onError={(err) => {
            const msg = (err && err.message) || 'Unexpected PayPal error';
            setErrorMsg(msg);
            setStatus('error');
          }}
        />
      </PayPalScriptProvider>
    </div>
  );
};

export default PayPalCheckout;
